import { vec3 } from 'gl-matrix';
import { Light } from './Light';
import { DirectionalLight } from './DirectionalLight';
import { FrumstumContainment, ViewFrustum } from '../util';
import { Context } from '../context/Context';
import { Scene } from '../scene/Scene';
import { DrawableObject, RenderMode } from '../renderer';

export type ShadowStats = {
  staticLights: number;
  dynamicLights: number;
  noneLights: number;
  shadowMapsRendered: number;
  dirtyCasters: number;
  // CPU-time breakdown of renderShadowMaps. Populated each frame; sums to
  // approximately Engine.shadowCpuMs (the wall-clock measurement).
  lightLoopMs: number;
  getNonStaticMs: number;
  renderStaticMs: number;
  renderDynamicMs: number;
};

export class LightManager {
  private _lights: Light[];
  public shadowBudget: number;

  /** Read-only view of the registered lights. Mutate via addLight/removeLight. */
  public get lights(): readonly Light[] {
    return this._lights;
  }

  public addLight(light: Light): void {
    this._lights.push(light);
  }

  public removeLight(light: Light): boolean {
    const idx = this._lights.indexOf(light);
    if (idx < 0) return false;
    this._lights.splice(idx, 1);
    return true;
  }
  public shadowStats: ShadowStats = {
    staticLights: 0,
    dynamicLights: 0,
    noneLights: 0,
    shadowMapsRendered: 0,
    dirtyCasters: 0,
    lightLoopMs: 0,
    getNonStaticMs: 0,
    renderStaticMs: 0,
    renderDynamicMs: 0,
  };

  constructor(public ambientColor: vec3) {
    this._lights = [];
    this.shadowBudget = 4;
  }

  private visibleLightsResult: Light[] = [];

  public getVisibleLights(frustum: ViewFrustum): Light[] {
    const result = this.visibleLightsResult;
    result.length = 0;
    for (const light of this._lights) {
      if (!light.enabled) continue;
      if (
        light instanceof DirectionalLight ||
        frustum.containsBoundingSphere(light.boundingSphere!) !== FrumstumContainment.None
      ) {
        result.push(light);
      }
    }
    return result;
  }

  private dynamicLightsResult: Light[] = [];

  public renderShadowMaps(ctx: Context, scene: Scene, cameraFrustum: ViewFrustum): void {
    const stats = this.shadowStats;
    stats.lightLoopMs = 0;
    stats.getNonStaticMs = 0;
    stats.renderStaticMs = 0;
    stats.renderDynamicMs = 0;
    stats.shadowMapsRendered = 0;

    const loopStart = performance.now();
    let staticCount = 0;
    let dynamicCount = 0;
    let noneCount = 0;
    let staticRenderMs = 0;

    const dynamicLights = this.dynamicLightsResult;
    dynamicLights.length = 0;

    for (const light of this._lights) {
      if (!light.enabled) continue;

      switch (light.shadowMode) {
        case 'static':
          staticCount++;
          if (light.shadowDirty) {
            // Refresh the light camera's matrices before rendering — without
            // this, moving a static light leaves `modelViewMatrix` /
            // `projectionMatrix` / `frustum` at construction-time values, so
            // the shadow map is re-rendered with stale camera but the lighting
            // pass uses the moved light position, producing a "shadow only
            // matches when the light is at its original position" artifact.
            // The dynamic path already does this (see below).
            light.camera.setup();
            const t = performance.now();
            light.renderShadowMap(ctx);
            staticRenderMs += performance.now() - t;
            light.shadowDirty = false;
            stats.shadowMapsRendered++;
          }
          break;
        case 'dynamic':
          dynamicCount++;
          if (
            light instanceof DirectionalLight ||
            cameraFrustum.containsBoundingSphere(light.boundingSphere!) !==
              FrumstumContainment.None
          ) {
            dynamicLights.push(light);
          }
          break;
        default:
          noneCount++;
          break;
      }
    }

    stats.staticLights = staticCount;
    stats.dynamicLights = dynamicCount;
    stats.noneLights = noneCount;
    stats.renderStaticMs = staticRenderMs;
    // The light loop covers everything except the static-light render itself.
    stats.lightLoopMs = performance.now() - loopStart - staticRenderMs;

    if (dynamicLights.length === 0) {
      stats.dirtyCasters = 0;
      return;
    }

    // Check if any non-static shadow casters are in each dynamic light's frustum
    const nonStaticStart = performance.now();
    const nonStaticCasters = this.getNonStaticShadowCasters(scene);
    stats.dirtyCasters = nonStaticCasters.length;
    for (const light of dynamicLights) {
      if (!light.shadowDirty) {
        this.checkDirtyFromCasters(light, nonStaticCasters);
      }
    }
    stats.getNonStaticMs = performance.now() - nonStaticStart;

    // Rank dirty dynamic lights: lights without a shadow map yet get absolute
    // priority (initial render), then by priority (desc). Without this, caster-
    // triggered re-renders of existing shadow maps consume the entire budget and
    // starve lights that haven't been rendered yet.
    const dirtyDynamic = dynamicLights
      .filter((l) => l.shadowDirty)
      .sort((a, b) => {
        const aNew = a.shadowMap ? 0 : 1;
        const bNew = b.shadowMap ? 0 : 1;
        if (aNew !== bNew) return bNew - aNew;
        return b.priority - a.priority;
      });

    const dynamicStart = performance.now();
    const remaining = this.shadowBudget - stats.shadowMapsRendered;
    for (let i = 0; i < Math.min(dirtyDynamic.length, remaining); i++) {
      const light = dirtyDynamic[i];
      light.camera.setup();
      light.renderShadowMap(ctx);
      light.shadowDirty = false;
      stats.shadowMapsRendered++;
    }
    stats.renderDynamicMs = performance.now() - dynamicStart;
  }

  private getNonStaticShadowCasters(scene: Scene): DrawableObject[] {
    // ObjectManager keeps a cached, lazily-rebuilt list. The previous
    // implementation walked every registered object every shadow frame,
    // which scaled linearly with scene size — at 100K scatter trees the
    // walk alone was ~5ms while the result is ~50 dynamic casters.
    return scene.objectManager.getNonStaticDepthCasters();
  }

  public destroy(gl: WebGL2RenderingContext): void {
    for (const light of this._lights) {
      if (light.shadowMap?.glTexture) {
        gl.deleteTexture(light.shadowMap.glTexture);
      }
    }
    this._lights = [];
  }

  private checkDirtyFromCasters(light: Light, casters: DrawableObject[]): void {
    // Directional lights have no bounds — always dirty if any caster exists
    if (light instanceof DirectionalLight) {
      if (casters.length > 0) light.shadowDirty = true;
      return;
    }

    for (const caster of casters) {
      if (!caster.bounds) continue;
      // Use the light's bounding sphere (position + outerRange) for the overlap check,
      // not the camera frustum which only covers one cubemap face for point lights
      if (light.boundingSphere!.intersects(caster.bounds.boundingSphere)) {
        light.shadowDirty = true;
        return;
      }
    }
  }
}
